import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  followChannel,
  unfollowChannel,
} from '~/services/commissioner/commissioner-service.service.api';
import { reportEvent } from '~/domains/analytics';
import type { UserEntrant } from '~/domains/contest/domains/services/interfaces/entrant.interface';
import type { PaginatedData } from '~/domains/services/interfaces/paginated-data.interface';

type UseEntrantFollowProps = {
  source:
    | 'contest_detail_entrants'
    | 'upcoming_entrants'
    | 'gamecenter_standings_slates'
    | 'marketplace_offers';
  contestId?: string;
};

export const useEntrantFollow = ({ source, contestId }: UseEntrantFollowProps) => {
  const [selectedEntrant, setSelectedEntrant] = useState<UserEntrant | null>(null);

  const queryClient = useQueryClient();

  const followMutation = useMutation({
    mutationFn: async ({ entrantId, channelId }: { entrantId: string; channelId: string }) => {
      await followChannel(channelId);
      return { entrantId, channelId };
    },
    onMutate: async ({ entrantId, channelId }) => {
      await queryClient.cancelQueries({ queryKey: ['contestEntrantsInfinite', contestId, source] });

      const previousEntrants = queryClient.getQueryData<{ pages: PaginatedData<UserEntrant>[] }>([
        'contestEntrantsInfinite',
        contestId,
        source,
      ]);

      if (previousEntrants && previousEntrants.pages) {
        queryClient.setQueryData<{ pages: PaginatedData<UserEntrant>[] }>(
          ['contestEntrantsInfinite', contestId, source],
          {
            ...previousEntrants,
            pages: previousEntrants.pages.map((page) => ({
              ...page,
              data: page.data.map((entrant) => {
                if (entrant.id === entrantId) {
                  return {
                    ...entrant,
                    isFollowing: true,
                  };
                }
                return entrant;
              }),
            })),
          }
        );
      }

      reportEvent('User Followed', {
        source,
        entrantId,
        channelId,
      });

      return { previousEntrants };
    },
    onError: (error, variables, context) => {
      if (context?.previousEntrants) {
        queryClient.setQueryData(
          ['contestEntrantsInfinite', contestId, source],
          context.previousEntrants
        );
      }
    },
    onSuccess: ({ entrantId }) => {
      queryClient.invalidateQueries({ queryKey: ['commissioner/channel', entrantId] });
    },
    // Callbacks sit on the mutation (not the mutate() call) to survive caller unmount, and the list
    // is left uninvalidated so a refetch can't overwrite the optimistic state with stale flags.
  });

  const unfollowMutation = useMutation({
    mutationFn: async ({ entrantId, channelId }: { entrantId: string; channelId: string }) => {
      await unfollowChannel(channelId);
      return { entrantId, channelId };
    },
    onMutate: async ({ entrantId, channelId }) => {
      await queryClient.cancelQueries({ queryKey: ['contestEntrantsInfinite', contestId, source] });

      const previousEntrants = queryClient.getQueryData<{ pages: PaginatedData<UserEntrant>[] }>([
        'contestEntrantsInfinite',
        contestId,
        source,
      ]);

      if (previousEntrants && previousEntrants.pages) {
        queryClient.setQueryData<{ pages: PaginatedData<UserEntrant>[] }>(
          ['contestEntrantsInfinite', contestId, source],
          {
            ...previousEntrants,
            pages: previousEntrants.pages.map((page) => ({
              ...page,
              data: page.data.map((entrant) => {
                if (entrant.id === entrantId) {
                  return {
                    ...entrant,
                    isFollowing: false,
                  };
                }
                return entrant;
              }),
            })),
          }
        );
      }

      reportEvent('User Unfollowed', {
        source,
        entrantId,
        channelId,
      });

      return { previousEntrants };
    },
    onError: (error, variables, context) => {
      if (context?.previousEntrants) {
        queryClient.setQueryData(
          ['contestEntrantsInfinite', contestId, source],
          context.previousEntrants
        );
      }
    },
    onSuccess: ({ entrantId }) => {
      queryClient.invalidateQueries({ queryKey: ['commissioner/channel', entrantId] });
    },
  });

  const handleFollow = (entrantId: string, channelId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    followMutation.mutate({ entrantId, channelId });
  };

  const handleUnfollow = (entrantId: string, channelId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    unfollowMutation.mutate({ entrantId, channelId });
  };

  const isFollowing = (entrantId: string) => {
    const cachedData = queryClient.getQueryData<{ pages: PaginatedData<UserEntrant>[] }>([
      'contestEntrantsInfinite',
      contestId,
      source,
    ]);

    if (!cachedData || !cachedData.pages) return false;

    // Find the entrant in any page
    for (const page of cachedData.pages) {
      const entrant = page.data.find((e) => e.id === entrantId);
      if (entrant) {
        return entrant.isFollowing || false;
      }
    }

    return false;
  };

  return {
    follow: {
      handleFollow,
      isLoading: followMutation.isLoading,
      isFollowing,
    },
    unfollow: {
      handleUnfollow,
      isLoading: unfollowMutation.isLoading,
      isFollowing,
    },
    selectedEntrant,
    setSelectedEntrant,
  };
};
